import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import {
  RESTAURANT_USER_ROLE,
  useAuthUserRestaurant,
  useGetRestaurantEmployeesQuery,
} from '@durma-soft/gros-sdk';

import { Link } from '@/components/shared/link';
import { SearchInput } from '@/components/shared/search-input';
import { DataTable, DataTableHTML } from '@/components/shared/data-table';
import { buttonVariants } from '@/components/shared/shadcn-ui/button';
import { EditRestaurantEmployeeModal } from '@/components/restaurant/employees/edit-restaurant-employee-modal';
import { CreateRestaurantEmployeeModal } from '@/components/restaurant/employees/create-restaurant-employee-modal';

import { columns, printColumns } from '@/config/restaurant/employees';
import { useDebouncedSearch } from '@/hooks/use-debounced-search';
import { PrintButton } from '@/components/shared/print-button';

import print from '@/lib/print/print';
import { cn } from '@/utils/helpers';

const RestaurantEmployees = () => {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const { employeeId } = useParams();

  const [memoizedColumns, memoizedPrintColumns] = useMemo(() => {
    return [columns(t), printColumns(t)];
  }, [t]);

  const restaurant = useAuthUserRestaurant();
  const hasEditPermission = restaurant.role !== RESTAURANT_USER_ROLE.EMPLOYEE;

  const { searchInput, debouncedSearchInput, handleChange } =
    useDebouncedSearch();

  const { data, isLoading, isPlaceholderData } = useGetRestaurantEmployeesQuery(
    {
      restaurant_id: restaurant.id,
      q: debouncedSearchInput,
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      sort_by: sorting[0]?.id,
      sort_order: sorting[0]?.desc ? 'desc' : 'asc',
    },
  );

  return (
    <>
      <Helmet>
        <title>{t('restaurant-employee.title')}</title>
      </Helmet>
      <div>
        <div className="flex items-center">
          <h1 className="text-lg font-semibold md:text-2xl">
            {t('restaurant-employee.title')}
          </h1>
        </div>
        <div
          className={cn(
            'flex flex-col gap-3',
            isPlaceholderData && 'opacity-50',
          )}
        >
          <div className="self-end flex gap-2">
            <PrintButton
              disabled={isLoading}
              variant="outline"
              onClick={() =>
                print({
                  content: (
                    <DataTableHTML
                      columns={memoizedPrintColumns}
                      data={data?.data}
                      pagination={pagination}
                    />
                  ),
                })
              }
            />
            <Link
              to="/restaurant/employees/create"
              className={buttonVariants({ variant: 'default' })}
            >
              {t('restaurant-employee.create')}
            </Link>
          </div>
          <div className="flex flex-col gap-3">
            <SearchInput
              className="self-start p-0"
              placeholder={t('common.search-placeholder')}
              searchInput={searchInput}
              handleChange={(event) => {
                handleChange(event);
                setPagination({ pageIndex: 0, pageSize: pagination.pageSize });
              }}
            />
            <DataTable
              columns={memoizedColumns}
              data={data?.data}
              pageCount={data?.total_pages}
              serverSideSorting
              sorting={sorting}
              setSorting={setSorting}
              pagination={pagination}
              isLoading={isLoading}
              setPagination={setPagination}
              columnVisibility={{
                edit: hasEditPermission,
              }}
            />
          </div>
        </div>
      </div>
      <CreateRestaurantEmployeeModal />
      {Boolean(employeeId) && <EditRestaurantEmployeeModal key={employeeId} />}
    </>
  );
};

export default RestaurantEmployees;
